import { CustomFurnitureData } from './furniture';

/**
 * Bibliothek eigener Möbel: dauerhaft im Browser (localStorage). Zusätzlich betten
 * Projektdateien ihre Bibliothek ein — beim Laden wird zusammengeführt, sodass
 * gezeichnete Möbel mit dem Projekt weitergegeben werden können.
 */

const LIB_KEY = 'campsketch.furniture.v1';

export function listCustomFurniture(): CustomFurnitureData[] {
  try {
    const raw = localStorage.getItem(LIB_KEY);
    if (!raw) return [];
    const parsed: unknown = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    return parsed
      .map((entry) => sanitizeCustomFurniture(entry))
      .filter((t): t is CustomFurnitureData => t !== null);
  } catch {
    return [];
  }
}

/** Fügt ein eigenes Möbel hinzu (id muss gesetzt sein) und speichert. */
export function addCustomFurniture(tpl: CustomFurnitureData): CustomFurnitureData[] {
  const list = listCustomFurniture().filter((t) => t.id !== tpl.id);
  list.push(tpl);
  persist(list);
  return list;
}

export function removeCustomFurniture(id: string): CustomFurnitureData[] {
  const list = listCustomFurniture().filter((t) => t.id !== id);
  persist(list);
  return list;
}

/** Führt fremde (z. B. aus einer Projektdatei) Vorlagen per id zusammen. */
export function mergeCustomFurniture(rawList: unknown[]): CustomFurnitureData[] {
  const list = listCustomFurniture();
  const known = new Set(list.map((t) => t.id));
  let changed = false;
  for (const raw of rawList) {
    const tpl = sanitizeCustomFurniture(raw);
    if (tpl && !known.has(tpl.id)) {
      list.push(tpl);
      known.add(tpl.id);
      changed = true;
    }
  }
  if (changed) persist(list);
  return list;
}

function persist(list: CustomFurnitureData[]): void {
  try {
    localStorage.setItem(LIB_KEY, JSON.stringify(list));
  } catch {
    // Speicher voll / privater Modus — Bibliothek bleibt dann nur für diese Sitzung
  }
}

/**
 * Prüft einen Eintrag vollständig (auch nach dem JSON-Rundgang: undefined-Farben
 * fehlen dann im Array) — ungültige Einträge werden verworfen.
 */
export function sanitizeCustomFurniture(raw: unknown): CustomFurnitureData | null {
  if (!raw || typeof raw !== 'object') return null;
  const t = raw as Record<string, unknown>;
  if (typeof t.id !== 'string' || t.id.length === 0) return null;
  if (typeof t.name !== 'string' || t.name.length === 0) return null;
  if (typeof t.note !== 'string') return null;
  for (const key of ['length', 'depth', 'height'] as const) {
    if (typeof t[key] !== 'number' || !Number.isFinite(t[key]!) || (t[key] as number) < 0) return null;
  }
  const verts = t.vertices;
  if (!Array.isArray(verts) || verts.length < 3) return null;
  for (const v of verts) {
    if (!Array.isArray(v) || v.length !== 3 || !v.every((n) => typeof n === 'number' && Number.isFinite(n))) return null;
  }
  const inRange = (i: unknown): i is number => typeof i === 'number' && Number.isInteger(i) && i >= 0 && i < verts.length;
  const ringOk = (ring: unknown): ring is number[] => Array.isArray(ring) && ring.length >= 3 && ring.every(inRange);
  if (!Array.isArray(t.faces)) return null;
  const faces: [number[], number[][], string | undefined][] = [];
  for (const f of t.faces) {
    if (!Array.isArray(f) || f.length < 2) return null;
    if (!ringOk(f[0])) return null;
    const holes = Array.isArray(f[1]) ? f[1] : [];
    if (!holes.every(ringOk)) return null;
    const color = f.length > 2 && typeof f[2] === 'string' ? (f[2] as string) : undefined;
    faces.push([f[0] as number[], holes as number[][], color]);
  }
  if (faces.length === 0) return null;
  if (!Array.isArray(t.looseEdges)) return null;
  const looseEdges: [number, number][] = [];
  for (const e of t.looseEdges) {
    if (!Array.isArray(e) || e.length !== 2 || !inRange(e[0]) || !inRange(e[1])) return null;
    looseEdges.push([e[0], e[1]]);
  }
  return {
    id: t.id,
    name: t.name,
    note: t.note,
    length: t.length as number,
    depth: t.depth as number,
    height: t.height as number,
    vertices: verts as [number, number, number][],
    faces,
    looseEdges
  };
}
